<template>
  <div class="max-w-[90vw] mx-auto">
    <div class="w-full flex justify-between align-center">
      <v-container class="onest-font">
        <a
          @click.prevent="goBack"
          style="color: inherit; cursor: pointer; text-decoration: none"
          class="d-flex align-center d-inline-flex"
        >
          <v-icon size="20" class="mr-1">mdi-arrow-u-left-bottom</v-icon>
          <span style="text-decoration: underline">voltar</span>
        </a>
        <h1 class="font-bold mt-2 text-5xl">Detalhes da Validação</h1>
      </v-container>
      <div class="w-full flex justify-end gap-4 align-center mt-6 pr-4">
        <v-btn
          v-if="validacao"
          :to="'/create-validation-period/' + validacao.id_validacao"
          color="primary"
          variant="flat"
          prepend-icon="mdi-pencil"
        >
          Editar Validação
        </v-btn>
        <v-chip
          v-if="validacao"
          :color="statusChipColor(validacao.situacao)"
          class="font-medium text-lg px-4 py-2"
          size="large"
          variant="tonal"
        >
          {{ formatarStatusValidacao(validacao.situacao) }}
        </v-chip>
      </div>
    </div>

    <v-container v-if="loading">
      <p>Carregando detalhes...</p>
    </v-container>

    <v-container v-else-if="validacao">
      <div class="w-full">
        <div>
          <h2 class="text-2xl font-bold">Informações básicas</h2>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <p class="mt-5 font-medium text-gray-700">Nome da Validação</p>
              <div class="text-input mt-2 rounded-lg border border-gray-200">
                {{ validacao.nome_validacao }}
              </div>
            </div>
            <div>
              <p class="mt-5 font-medium text-gray-700">Projetos Envolvidos</p>
              <div class="mt-2 flex flex-wrap gap-2">
                <v-chip
                  v-for="projeto in validacao.tb_projetos_envolvidos"
                  :key="projeto.id_projeto"
                  color="primary"
                  variant="flat"
                >
                  {{ projeto.tb_projeto.nome }}
                </v-chip>
                <span
                  v-if="
                    !validacao.tb_projetos_envolvidos ||
                    validacao.tb_projetos_envolvidos.length === 0
                  "
                  class="text-gray-500"
                >
                  Nenhum projeto associado.
                </span>
              </div>
            </div>
            <div>
              <p class="mt-5 font-medium text-gray-700">
                Formulário de Validação
              </p>
              <div class="text-input mt-2 rounded-lg border border-gray-200">
                <span v-if="loadingFormulario" class="text-gray-500"
                  >Carregando...</span
                >
                <span v-else>{{ nomeFormulario }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="w-full mt-8">
          <h2 class="text-2xl font-bold">
            Cronograma da validação (Datas finais de cada etapa)
          </h2>
          <div class="grid grid-cols-2 gap-4 mt-4 w-fit">
            <div class="validation-step flex-col items-start gap-1">
              <p class="w-72 m-0 font-medium">1. Preparação</p>
              <p class="text-gray-600">
                {{ formatarData(validacao.data_final_preparacao) }}
              </p>
            </div>

            <div
              class="validation-step flex-col items-start gap-1"
              :class="{ 'opacity-50': !validacao.flag_validacao_roteiro }"
            >
              <p class="w-72 m-0 font-medium">2. Validação de roteiros</p>
              <p class="text-gray-600">
                {{
                  validacao.flag_validacao_roteiro
                    ? formatarData(validacao.data_final_validacao_roteiro)
                    : 'Etapa não habilitada'
                }}
              </p>
            </div>

            <div class="validation-step flex-col items-start gap-1">
              <p class="w-72 m-0 font-medium">3. Execução da validação</p>
              <p class="text-gray-600">
                {{ formatarData(validacao.data_final_execucao_validacao) }}
              </p>
            </div>

            <div class="validation-step flex-col items-start gap-1">
              <p class="w-72 m-0 font-medium">4. Identificação de pendências</p>
              <p class="text-gray-600">
                {{
                  formatarData(validacao.data_final_identificacao_pendencias)
                }}
              </p>
            </div>

            <div class="validation-step flex-col items-start gap-1">
              <p class="w-72 m-0 font-medium">5. Priorização de pendências</p>
              <p class="text-gray-600">
                {{ formatarData(validacao.data_final_priorizacao_pendencias) }}
              </p>
            </div>

            <div
              class="validation-step flex-col items-start gap-1"
              :class="{
                'opacity-50': !validacao.flag_etapa_especifica_correcoes,
              }"
            >
              <p class="w-72 m-0 font-medium">6. Correções</p>
              <p class="text-gray-600">
                {{
                  validacao.flag_etapa_especifica_correcoes
                    ? formatarData(validacao.data_final_correcoes)
                    : 'Etapa não habilitada'
                }}
              </p>
            </div>

            <div class="validation-step flex-col items-start gap-1">
              <p class="w-72 m-0 font-medium">7. Validação de correções</p>
              <p class="text-gray-600">
                {{ formatarData(validacao.data_final_validacao_correcoes) }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </v-container>

    <v-container v-else>
      <v-alert
        type="error"
        text="Não foi possível carregar os detalhes da validação."
      ></v-alert>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  name: 'ValidationDetails',
  data() {
    return {
      validacao: null,
      loading: true,
      nomeFormulario: 'Desconhecido',
      loadingFormulario: false,
    };
  },
  mounted() {
    this.carregarValidacao();
  },
  methods: {
    async carregarValidacao() {
      try {
        const id = this.$route.params.id;
        const response = await axios.get(`${apiUrl}/api/v1/validacoes/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        this.validacao = response.data;

        if (
          this.validacao.tb_formulario_validacao &&
          this.validacao.tb_formulario_validacao.titulo
        ) {
          this.nomeFormulario = this.validacao.tb_formulario_validacao.titulo;
        } else if (this.validacao.id_formulario) {
          this.loadingFormulario = true;
          try {
            const formResponse = await axios.get(
              `${apiUrl}/api/v1/formularioValidacao`,
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                },
              },
            );
            const forms = formResponse.data;
            const found = forms.find(
              (f) => f.id_formulario_validacao === this.validacao.id_formulario,
            );
            if (found) {
              this.nomeFormulario = found.titulo;
            } else {
              this.nomeFormulario = 'Formulário não encontrado';
            }
          } catch (err) {
            console.error('Erro ao carregar formulários:', err);
            this.nomeFormulario = 'Erro ao carregar formulário';
          } finally {
            this.loadingFormulario = false;
          }
        }
      } catch (error) {
        console.error('Erro ao carregar validação:', error);
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    formatarData(dataIso) {
      if (!dataIso) {
        return '-';
      }
      const data = new Date(dataIso);
      data.setMinutes(data.getMinutes() + data.getTimezoneOffset());

      return data.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    statusChipColor(status) {
      const colors = {
        EDICAO: '#9CA3AF',
        PREPARACAO: '#60A5FA',
        VALIDACAO_ROTEIROS: '#3B82F6',
        USUARIOS_TESTANDO: '#A78BFA',
        IDENTIFICACAO_PENDENCIAS: '#FBBF24',
        PRIORIZACAO_PENDENCIAS: '#F97316',
        CORRECOES: '#F87171',
        VALIDACAO_CORRECOES: '#34D399',
        VALIDACAO_FINALIZADA: '#10B981',
      };

      return colors[status];
    },
    formatarStatusValidacao(statusDoBanco) {
      const mapaStatus = {
        EDICAO: 'Em Edição',
        PREPARACAO: 'Em Preparação',
        VALIDACAO_ROTEIROS: 'Validação de Roteiros',
        USUARIOS_TESTANDO: 'Usuários Testando',
        IDENTIFICACAO_PENDENCIAS: 'Identificação de Pendências',
        PRIORIZACAO_PENDENCIAS: 'Priorização de Pendências',
        CORRECOES: 'Correções',
        VALIDACAO_CORRECOES: 'Validação de Correções',
        VALIDACAO_FINALIZADA: 'Validação Finalizada',
      };

      return mapaStatus[statusDoBanco] || statusDoBanco;
    },
  },
};
</script>

<style scoped>
.validation-step {
  @apply flex gap-1 bg-white p-4 rounded-xl shadow-sm border border-gray-100;
}

.text-input {
  @apply bg-white p-3 text-gray-800;
}
</style>
